<template>
  <v-card elevation="2" v-resize="onResize" class="pa-4">
    <div class="d-flex">
      <v-spacer></v-spacer>
      <div class="mx-n2">
        <VisibilityToggleButton
          v-model="showTrendLines"
          :show-text="$t('trends.showTrendLines')"
          :hide-text="$t('trends.hideTrendLines')"
        />
        <InstructionIcon v-model="showInstruction" />
        <InstructionList
          :show="showInstruction"
          :list="$t('trends.instructions')"
        />
      </div>
    </div>
    <div
      v-show="showTrendLines"
      class="flex-wrap align-end mt-2"
      :class="{ 'd-flex': showTrendLines }"
    >
      <ModeToggleButtons
        class="mt-4"
        v-model="yValueMode"
        :modes="yValueModes"
        :label="$t('trends.yValueMode')"
        id="trends-y-value-mode"
        ><div class="ml-n2">
          <HelpIcon>{{ $t("trends.yValueModeHelpText") }}</HelpIcon>
        </div>
      </ModeToggleButtons>
      <ItemsControl
        v-model="selectedItemKeys"
        :items="items"
        class="flex-grow-1 mx-2"
      />
    </div>
    <div
      class="flex-wrap align-center mt-2"
      :class="{ 'd-flex': showTrendLines }"
      v-show="showTrendLines"
    >
      <div>
        <ResetButton
          :show="isBrushDirty"
          @reset="resetBrush"
          :text="$t('trends.resetBrush')"
        />
      </div>
      <v-spacer></v-spacer>
      <div>
        <ItemsToggle
          :items="lineData"
          :is-item-opaque="isItemOpaque"
          @enter-item="itemEntered"
          @leave-item="itemLeft"
        />
      </div>
    </div>
    <div v-show="showTrendLines">
      <FocusChart
        v-if="items.length > 0"
        :width="width"
        :brushed-year-range="brushedYearRange"
        :line-data="lineData"
        :is-item-opaque="isItemOpaque"
        :y-value-mode="yValueMode"
        :format-y-tick="formatYTick"
        @enter-dot="dotEntered"
        @leave-dot="dotLeft"
      />
      <ContextChart
        v-if="items.length > 0"
        :width="width"
        :year-range="yearRange"
        :brushed-year-range="brushedYearRange"
        :line-data="lineData"
        :is-item-opaque="isItemOpaque"
        :y-value-mode="yValueMode"
        :format-y-tick="formatYTick"
        @brush-ended="brusheEnded"
      />
    </div>
    <Tooltip
      :dot-data="dotData"
      :show="showTooltip"
      :position="pointerPosition"
    />
  </v-card>
</template>

<script>
import ItemsToggle from "./ItemsToggle";
import FocusChart from "./FocusChart";
import ContextChart from "./ContextChart";
import Tooltip from "./Tooltip.vue";
import ItemsControl from "./ItemsControl.vue";
import ResetButton from "@/components/ResetButton.vue";
import VisibilityToggleButton from "@/components/VisibilityToggleButton.vue";
import InstructionList from "@/components/InstructionList.vue";
import InstructionIcon from "@/components/InstructionIcon.vue";
import ModeToggleButtons from "@/components/ModeToggleButtons";
import HelpIcon from "@/components/HelpIcon.vue";
import { mapGetters, mapState } from "vuex";
import { getItems } from "@/utils/process-line-data";
import { extent } from "d3-array";
import { format } from "d3-format";

const extraColors = [
  "#E58606",
  "#5D69B1",
  "#52BCA3",
  "#99C945",
  "#CC61B0",
  "#24796C",
  "#DAA51B",
  "#2F8AC4",
  "#764E9F",
  "#ED645A",
  "#CC3A8E",
  "#A5AA99"
];

export default {
  components: {
    ItemsToggle,
    FocusChart,
    ContextChart,
    Tooltip,
    ResetButton,
    VisibilityToggleButton,
    InstructionIcon,
    InstructionList,
    ItemsControl,
    ModeToggleButtons,
    HelpIcon
  },
  data: () => ({
    width: 1200,
    brushedYearRange: [],
    showTooltip: false,
    pointerPosition: [0, 0],
    dotData: null,
    showTrendLines: true,
    showInstruction: false,
    highlightedItemKey: null,
    selectedItemKeys: [],
    yValueMode: "count" // ["count", "percentageYear", "percentageTotal"]
  }),
  computed: {
    ...mapState(["allYears", "poems"]),
    ...mapGetters(["sankeyThemesSet", "colorScale"]),
    yearRange() {
      return extent(this.allYears);
    },
    items() {
      return getItems(this.poems);
    },
    lineData() {
      const lineData = this.selectedItemKeys.map(key => {
        const d = this.items.find(e => e.key === key);
        return Object.assign({}, d);
      });
      lineData
        .filter(d => this.sankeyThemesSet.has(d.value))
        .forEach(d => (d.color = this.colorScale(d.value)));
      lineData
        .filter(d => !this.sankeyThemesSet.has(d.value))
        .forEach((d, i) => (d.color = extraColors[i % extraColors.length]));
      return lineData;
    },
    isBrushDirty() {
      return (
        JSON.stringify(this.yearRange) !== JSON.stringify(this.brushedYearRange)
      );
    },
    formatYTick() {
      switch (this.yValueMode) {
        case "count":
          return format(",");
        case "percentageYear":
        case "percentageTotal":
          return format("~%");
        default:
          return d => d;
      }
    },
    yValueModes() {
      return ["count", "percentageYear", "percentageTotal"].map(mode => ({
        text: this.$t(`trends.${mode}`),
        value: mode
      }));
    }
  },
  watch: {
    sankeyThemesSet(value) {
      this.selectedItemKeys = [...value].map(d => `${d} (theme)`);
    },
    allYears() {
      this.resetBrush();
    }
  },
  mounted() {
    this.onResize();
  },
  methods: {
    onResize() {
      this.width = this.$el.clientWidth;
    },
    resetBrush() {
      this.brushedYearRange = this.yearRange.slice();
    },
    brusheEnded(brushedYearRange) {
      this.brushedYearRange = brushedYearRange;
    },
    itemEntered(d) {
      this.highlightedItemKey = d.key;
    },
    itemLeft() {
      this.highlightedItemKey = null;
    },
    dotEntered(d, event) {
      this.pointerPosition = [event.clientX + 8, event.clientY + 8];
      this.dotData = {
        year: d.year,
        count: d.count,
        percentageYear: d.percentageYear,
        percentageTotal: d.percentageTotal,
        keys: this.lineData
          .filter(
            e =>
              !!e.values.find(
                v =>
                  v.year === d.year && v[this.yValueMode] === d[this.yValueMode]
              )
          )
          .map(d => ({
            key: d.key,
            value: d.value,
            type: d.type,
            color: d.color
          }))
      };
      this.showTooltip = true;
    },
    dotLeft() {
      this.showTooltip = false;
    },
    isItemOpaque(key) {
      return this.highlightedItemKey ? this.highlightedItemKey !== key : false;
    }
  }
};
</script>

<style scoped></style>
